import { mapGetters } from "vuex";
import { getProductIdentifier } from "@/utils";
import { analyticPlatformsNames } from "~/config";

export default {
  computed: {
    ...mapGetters({
      integrationCallbackStatuses: "general/getIntegrationCallbackStatuses",
      getCollectionList: "general/getCollectionList",
      getCartItems: "cart/getCartItems",
      getCompanyName: "general/getCompanyName",
    }),
    storeSettings() {
      return this.$store.state.general.settings;
    },
    isGoogleAnalyticsAvailable() {
      return (
        this.integrationCallbackStatuses.TYPE_GOOGLE_ANALYTICS &&
        window?.dataLayer
      );
    },
  },
  methods: {
    getProductCollection(id) {
      if (!id) {
        return "";
      }
      const collection = this.getCollectionList?.find((item) => item.id === id);
      return collection?.mixedTranslation?.title || id;
    },
    googleAnalyticsPriceFormat(price) {
      price = price || 0;
      return Number(price).toFixed(2);
    },
    getProductCategory(product) {
      return product?.categories?.[0]?.mixedTranslation?.title ||
        product?.categories_ids?.[0] ||
        "";
    },
    getCoupon(item) {
      const discountCodes = item.options.discounts
        ? Object.keys(item.options.discounts)
        : [];
      const giftCardCodes = item.options.gift_cards
        ? Object.keys(item.options.gift_cards)
        : [];
      const codes = [...discountCodes, ...giftCardCodes];
      return codes?.length ? codes.join(", ") : "";
    },
    gaPurchaseEvent(data) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      const discountCodes = data.options.discounts
        ? Object.keys(data.options.discounts)
        : [];
      const giftCardCodes = data.options.gift_cards
        ? Object.keys(data.options.gift_cards)
        : [];
      const codes = [...discountCodes, ...giftCardCodes];
      window.dataLayer.push({
        event: "purchase",
        ecommerce: {
          transaction_id: data.po_number, // ідентифікатор транзакції
          affiliation: "Online Store", // де здійснюється покупка
          value: this.googleAnalyticsPriceFormat(data.final_amount), // сума транзакції
          tax: this.googleAnalyticsPriceFormat(data.tax_amount), // налоги, якщо вони є
          shipping: this.googleAnalyticsPriceFormat(data.shipping_amount), // ціна доставки, якщо вона є
          currency: data.currency_code, // валюта, якщо гривні, то UAH, якщо доллар, то USD
          coupon: codes?.length ? codes.join(", ") : "", // купон, якщо є
          items: data.order_products.map((item) => ({
            item_name: item.mixedTranslation.title, // назва товару
            item_id: getProductIdentifier(analyticPlatformsNames.google, {
              id: item.product_configuration_id || item.product_id || "",
              sku: item.configuration_id,
            }), // ідентифікатор товару
            price: this.googleAnalyticsPriceFormat(item.base_cost), // ціна товару
            item_brand: this.getCompanyName, // бренд товару

            item_category: this.getProductCategory(item), // категорія товару

            item_variant: item.configuration_id, // варіант товару
            quantity: item.quantity, // кількість замовленого товару
          })),
        },
      });
    },
    gaViewItemListEvent(data, extraData) {
      if (!this.isGoogleAnalyticsAvailable || !data?.length) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
          items: data.map((item, index) => ({
            item_name: item?.mixedTranslation?.title || item.title, // потрібно вказати ім’я або ідентифікатор
            item_id: getProductIdentifier(analyticPlatformsNames.google, {
              id: item.selected_config?.id || item.variant_plp_image?.product_configuration_id || item.default_config?.id || item.id || "",
              sku: item.selected_config?.configuration_id || item.variant_plp_image?.sku || item.default_sku || "",
            }), // ідентифікатор товару
            price: this.googleAnalyticsPriceFormat(item.selected_config?.cost || item.variant_plp_image?.cost || item.default_config?.cost || item.base_cost), // ціна товару
            item_brand: this.getCompanyName, // бренд товару
            item_category: this.getProductCategory(item), // категорія товару
            item_category2: "", // другий рівень категорії товару
            item_category3: "", // третій рівень категорії товару
            item_category4: "", // четвертий рівень категорії товару
            item_variant: item.selected_config?.configuration_id || item.variant_plp_image?.sku || item.default_sku || "", // варіант товару
            item_list_name: extraData?.item_list_name || "", // назва списку товарів
            item_list_id: item.selected_config?.configuration_id || item.variant_plp_image?.sku || item.default_sku || "", // унікальний ідентифікатор переліку товарів
            index: index + 1, // позиція товару у переліку товарів
            quantity: 1, // кількість замовленого товару
          })),
        },
      });
    },
    gaSelectItemEvent(data, extraData) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "select_item",
        ecommerce: {
          items: [
            {
              item_name: data?.mixedTranslation?.title || data.title, // потрібно вказати ім’я або ідентифікатор
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: data.selected_config?.id || data.variant_plp_image?.product_configuration_id || data.default_config?.id || data.id || "",
                sku: data.selected_config?.configuration_id || data.variant_plp_image?.sku || data.default_sku || "",
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(data.selected_config?.cost || data.variant_plp_image?.cost || data.default_config?.cost || data.base_cost), // ціна товару
              item_brand: this.getCompanyName, // бренд товару
              item_category: this.getProductCategory(data), // категорія товару
              item_category2: "", // другий рівень категорії товару
              item_category3: "", // третій рівень категорії товару
              item_category4: "", // четвертий рівень категорії товару
              item_variant: data.selected_config?.configuration_id || data.variant_plp_image?.sku || data.default_sku || "", // варіант товару
              item_list_name: extraData?.item_list_name || "", // назва списку товарів
              item_list_id: data.selected_config?.configuration_id || data.variant_plp_image?.sku || data.default_sku || "", // унікальний ідентифікатор переліку товарів
              index: extraData.index + 1, // позиція товару у переліку товарів
              quantity: 1, // кількість замовленого товару
            },
          ],
        },
      });
    },
    gaViewItemEvent(data, configurationData) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_item",
        ecommerce: {
          items: [
            {
              item_name: data?.mixedTranslation?.title || data?.title, // назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: configurationData?.id || data.id || "",
                sku: configurationData?.configuration_id || "",
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(
                configurationData?.cost || data.base_cost
              ), // ціна товару
              item_brand: this.getCompanyName, // бренд товару
              item_category: this.getProductCategory(data), // категорія товару
              item_category2: "", // другий рівень категорії товару
              item_category3: "", // третій рівень категорії товару
              item_category4: "", // четвертий рівень категорії товару
              item_variant: configurationData?.configuration_id || "", // варіант товару

              item_list_name: "", // назва списку товарів

              item_list_id: configurationData?.configuration_id || "", // унікальний ідентифікатор переліку товарів
              index: 1, // позиція товару у переліку товарів
              quantity: 1, // кількість замовленого товару
            },
          ],
          currency: this.storeSettings?.currency_code || "",
          value: this.googleAnalyticsPriceFormat(
            configurationData?.cost || data.base_cost
          ),
        },
      });
    },
    gaAddToCartEvent(data, configurationData) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_name: data?.mixedTranslation?.title || data?.title, // назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: configurationData?.id || data.id || "",
                sku: configurationData?.configuration_id || "",
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(
                configurationData?.cost || data.base_cost
              ), // ціна товару
              item_brand: this.getCompanyName, // бренд товару
              item_category: this.getProductCategory(data), // категорія товару
              item_category2: "", // другий рівень категорії товару
              item_category3: "", // третій рівень категорії товару
              item_category4: "", // четвертий рівень категорії товару
              item_variant: configurationData?.configuration_id || "", // варіант товару

              item_list_name: "", // назва списку товарів

              item_list_id: configurationData?.configuration_id || "", // унікальний ідентифікатор переліку товарів
              index: this.getCartItems?.length
                ? this.getCartItems.length + 1
                : 1, // позиція товару у переліку товарів
              quantity: 1, // кількість замовленого товару
            },
          ],
          currency: this.storeSettings?.currency_code || "",
          value: this.googleAnalyticsPriceFormat(
            configurationData?.cost || data.base_cost
          ),
        },
      });
    },
    gaRemoveFromCartEvent(data, index) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          items: [
            {
              item_name: data?.mixedTranslation?.title || data?.title, // назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: data.product_configuration_id || data.product_id || "",
                sku: data.configuration_id,
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(data.base_cost), // ціна товару
              item_brand: this.getCompanyName, // бренд товару

              item_category: this.getProductCategory(data), // категорія товару

              item_variant: data.configuration_id, // варіант товару

              item_list_name: "", // назва списку товарів

              item_list_id: data.configuration_id, // унікальний ідентифікатор переліку товарів
              index: index + 1, // позиція товару у переліку товарів
              quantity: data.quantity, // кількість замовленого товару
            },
          ],
        },
      });
    },
    gaBeginCheckoutEvent(data) {
      if (!this.isGoogleAnalyticsAvailable || !data?.items?.length) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          items: data.items.map((item, index) => ({
            item_name: item?.mixedTranslation?.title || item?.title, // назва товару
            item_id: getProductIdentifier(analyticPlatformsNames.google, {
              id: item.product_configuration_id || item.product_id || "",
              sku: item.configuration_id,
            }), // ідентифікатор товару
            price: this.googleAnalyticsPriceFormat(item.base_cost), // ціна товару
            item_brand: this.getCompanyName, // бренд товару

            item_category: this.getProductCategory(item), // категорія товару

            item_category2: "", // другий рівень категорії товару
            item_category3: "", // третій рівень категорії товару
            item_category4: "", // четвертий рівень категорії товару
            item_variant: item.configuration_id, // варіант товару

            item_list_name: "", // назва списку товарів

            item_list_id: item.configuration_id, // унікальний ідентифікатор переліку товарі
            index: index + 1, // позиція товару у переліку товарів
            quantity: item.quantity, // кількість замовленого товару
          })),
          currency: this.storeSettings?.currency_code || "",
          value: this.googleAnalyticsPriceFormat(data?.final_amount),
        },
      });
    },
    gaAddShippingInfoEvent(data, shippingMethodData) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_shipping_info",
        ecommerce: {
          shipping_tier: shippingMethodData?.deliveryServiceName || "", // Назва служби доставки
          value: this.googleAnalyticsPriceFormat(shippingMethodData?.price || 0), // Ціна доставки
          currency: this.storeSettings?.currency_code || "", // Валюта доставки
          items: data.items.map((item, index) => {
            return {
              item_name: item.title, // Назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: item.product_configuration_id || item.product_id || "",
                sku: item.configuration_id,
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(item.base_cost), // Ціна товару
              item_brand: this.getCompanyName, // Бренд товару

              item_category: this.getProductCategory(item), // Категорія товару

              item_category2: "", // Категорія товару	2
              item_category3: "", // Категорія товару	3
              item_category4: "", // Категорія товару	4
              item_variant: item.configuration_id, // Модифікація

              item_list_name: "", // Група товару

              item_list_id: item.configuration_id, // id групи товару
              index: index + 1, // Порядковий номер товару в категоріі
              quantity: item.quantity, // Кількість товару
              discount: item?.discount_amount || "", // Знижка
              coupon: this.getCoupon(item), // Купон
            };
          }),
        },
      });
    },
    gaAddPaymentInfoEvent(data, paymentType) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_payment_info",
        ecommerce: {
          items: data.items.map((item, index) => {
            return {
              item_name: item.title, // Назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: item.product_configuration_id || item.product_id || "",
                sku: item.configuration_id,
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(item.base_cost), // Ціна товару
              item_brand: this.getCompanyName, // Бренд товару

              item_category: this.getProductCategory(item), // Категорія товару

              item_category2: "", // Категорія товару	2
              item_category3: "", // Категорія товару	3
              item_category4: "", // Категорія товару	4
              item_variant: item.configuration_id, // Модифікація

              item_list_name: "", // Група товару

              item_list_id: item.configuration_id, // id групи товару
              index: index + 1, // Порядковий номер товару в категоріі
              quantity: item.quantity, // Кількість товару
              discount: item?.discount_amount || "", // Знижка
              coupon: this.getCoupon(item), // Купон
            };
          }),
          currency: this.storeSettings?.currency_code || "",
          value: this.googleAnalyticsPriceFormat(data?.final_amount),
          payment_type: paymentType || "",
          coupon: this.getCoupon(data),
        },
      });
    },
    gaDynamicParamsEvent(event, data) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }

      const { ecomm_prodid, ecomm_pagetype, ecomm_totalvalue, ecomm_category } =
        data;
      window.dataLayer.push({
        event: event || "Dynamic Remarketing",
        dynamicParams: {
          ecomm_prodid: ecomm_prodid || "",
          ecomm_pagetype: ecomm_pagetype || "",
          ecomm_totalvalue: ecomm_totalvalue || "",
          ecomm_category: ecomm_category || "",
        },
      });
    },
    gaViewCartEvent(data) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_cart",
        ecommerce: {
          value: this.googleAnalyticsPriceFormat(data?.final_amount), // сума транзакції
          currency: this.storeSettings?.currency_code || "", // валюта, якщо гривні, то UAH, якщо доллар, то USD
          items: data?.items?.map((item) => ({
            item_name: item?.mixedTranslation?.title, // назва товару
            item_id: getProductIdentifier(analyticPlatformsNames.google, {
              id: item.product_configuration_id || item.product_id || "",
              sku: item.configuration_id,
            }), // ідентифікатор товару
            price: this.googleAnalyticsPriceFormat(item.base_cost), // ціна товару
            item_brand: this.getCompanyName, // бренд товару
            item_category: this.getProductCategory(item), // категорія товару
            item_variant: item.configuration_id, // варіант товару
            quantity: item.quantity, // кількість замовленого товару
          })),
        },
      });
    },
    gaViewSearchResultEvent(data) {
      if (!this.isGoogleAnalyticsAvailable || !data) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_search_results",
        ecommerce: {
          search_term: data,
        },
      });
    },

    gaGenerateLeadEvent(lead_source) {
      if (!this.isGoogleAnalyticsAvailable || !lead_source) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "generate_lead",
        ecommerce: {
          currency: this.storeSettings?.currency_code || "",
          value: 0,
          lead_source,
        },
      });
    },

    gaAddToWishlistEvent(product) {
      if (!this.isGoogleAnalyticsAvailable || !product) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_wishlist",
        ecommerce: {
          currency: this.storeSettings?.currency_code || "",
          value: product.product_price,
          items: [
            {
              item_name: product?.product_title, // назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: product.product_configuration_id || product.product_id || "",
                sku: product.configuration_id,
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(product.product_price), // ціна товару
              item_brand: this.getCompanyName, // бренд товару
              item_category: this.getProductCategory(product), // категорія товару
              item_variant: product.configuration_id, // варіант товару
            },
          ],
        },
      });
    },

    gaRemoveFromWishlistEvent(product) {
      if (!this.isGoogleAnalyticsAvailable || !product) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "favorites_remove",
        ecommerce: {
          currency: this.storeSettings?.currency_code || "",
          value: product.product_price,
          items: [
            {
              item_name: product?.product_title, // назва товару
              item_id: getProductIdentifier(analyticPlatformsNames.google, {
                id: product.product_configuration_id || product.product_id || "",
                sku: product.configuration_id,
              }), // ідентифікатор товару
              price: this.googleAnalyticsPriceFormat(product.product_price), // ціна товару
              item_brand: this.getCompanyName, // бренд товару
              item_category: this.getProductCategory(product), // категорія товару
              item_variant: product.configuration_id, // варіант товару
            },
          ],
        },
      });
    },

    gaViewWishlistEvent(products) {
      if (!this.isGoogleAnalyticsAvailable || !products) {
        return;
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "favorites_view",
        ecommerce: {
          currency: this.storeSettings?.currency_code || "",
          value: products.reduce(
            (acc, product) => acc + Number(product.price),
            0
          ),
          items: products.map((product) => ({
            item_name: product?.mixedTranslation?.title, // назва товару
            item_id: getProductIdentifier(analyticPlatformsNames.google, {
              id: product.product_configuration_id || product.product_id || "",
              sku: product.configuration_id,
            }), // ідентифікатор товару
            price: this.googleAnalyticsPriceFormat(product.price), // ціна товару
            item_brand: this.getCompanyName, // бренд товару
            item_category: this.getProductCategory(product), // категорія товару
            item_variant: product.configuration_id, // варіант товару
          })),
        },
      });
    },
  },
};
